/**
 * Wraps `supabase.rpc` and `supabase.functions.invoke` with Sentry breadcrumbs
 * + automatic captureException for unhandled failures.
 *
 * No PII payloads are recorded — only the RPC name, duration, and error code/message.
 *
 * Imported once for side-effects from main.tsx, after initSentry().
 */
import { supabase } from '@/integrations/supabase/client';
import { Sentry } from './sentry';

type AnyArgs = unknown[];
type AnyFn = (...args: AnyArgs) => unknown;

let installed = false;

export function installSupabaseInstrumentation() {
  if (installed) return;
  installed = true;

  // ── RPC ────────────────────────────────────────────────────────────────────
  const originalRpc = supabase.rpc.bind(supabase) as AnyFn;
  (supabase as unknown as { rpc: AnyFn }).rpc = function patchedRpc(
    fn: string,
    params?: object,
    options?: object
  ) {
    const started = performance.now();
    const builder = originalRpc(fn, params, options) as Promise<{
      data: unknown;
      error: { message?: string; code?: string } | null;
    }>;

    // Supabase rpc returns a thenable PostgrestBuilder — preserve its chainability
    // by patching .then on the returned object instead of awaiting.
    const originalThen = (builder as unknown as { then: AnyFn }).then.bind(builder);
    (builder as unknown as { then: AnyFn }).then = function patchedThen(
      onFulfilled: AnyFn,
      onRejected?: AnyFn
    ) {
      return originalThen(
        (result: { data: unknown; error: { message?: string; code?: string } | null }) => {
          const duration = Math.round(performance.now() - started);
          const err = result?.error;
          Sentry.addBreadcrumb({
            category: 'supabase.rpc',
            level: err ? 'error' : 'info',
            message: `rpc ${fn} ${err ? 'failed' : 'ok'} (${duration}ms)`,
            data: {
              fn,
              duration_ms: duration,
              error_code: err?.code,
              error_message: err?.message?.slice(0, 200),
            },
          });
          if (err) {
            Sentry.captureMessage(`supabase.rpc ${fn} failed`, {
              level: 'warning',
              tags: { area: 'supabase', kind: 'rpc', rpc_fn: fn },
              extra: {
                error_code: err.code,
                error_message: err.message,
                duration_ms: duration,
              },
            });
          }
          return onFulfilled ? onFulfilled(result) : result;
        },
        (e: unknown) => {
          const duration = Math.round(performance.now() - started);
          Sentry.addBreadcrumb({
            category: 'supabase.rpc',
            level: 'error',
            message: `rpc ${fn} threw (${duration}ms)`,
            data: { fn, duration_ms: duration },
          });
          Sentry.captureException(e, {
            tags: { area: 'supabase', kind: 'rpc', rpc_fn: fn },
            extra: { duration_ms: duration },
          });
          if (onRejected) return onRejected(e);
          throw e;
        }
      );
    };
    return builder;
  };

  // ── Edge Functions ────────────────────────────────────────────────────────
  const fnsObj = supabase.functions as unknown as { invoke: AnyFn };
  const originalInvoke = fnsObj.invoke.bind(supabase.functions) as AnyFn;
  fnsObj.invoke = async function patchedInvoke(fnName: string, options?: { body?: unknown }) {
    const started = performance.now();
    try {
      const result = (await originalInvoke(fnName, options)) as {
        data: unknown;
        error: { message?: string } | null;
      };
      const duration = Math.round(performance.now() - started);
      const err = result?.error;
      Sentry.addBreadcrumb({
        category: 'supabase.fn',
        level: err ? 'error' : 'info',
        message: `fn ${fnName} ${err ? 'failed' : 'ok'} (${duration}ms)`,
        data: { fn: fnName, duration_ms: duration, error_message: err?.message?.slice(0, 200) },
      });
      if (err) {
        Sentry.captureMessage(`supabase.functions.invoke ${fnName} failed`, {
          level: 'warning',
          tags: { area: 'supabase', kind: 'edge_fn', edge_fn: fnName },
          extra: { error_message: err.message, duration_ms: duration },
        });
      }
      return result;
    } catch (e) {
      const duration = Math.round(performance.now() - started);
      Sentry.captureException(e, {
        tags: { area: 'supabase', kind: 'edge_fn', edge_fn: fnName },
        extra: { duration_ms: duration },
      });
      throw e;
    }
  };
}
